import Axios from 'axios';
import { useQueryClient, useMutation } from 'react-query';

import { useDetailsQuery, useListQuery } from 'base/api/hooks';
import { StandardError } from 'base/api/errors';
import apiUrls from 'base/api/urls';
import { CommonVars, PK } from 'common/types';
import { composeUrl } from 'common/utils';
import { BlogCreateVars } from './types';
import { Blog, Tags } from 'base/api/types';
import { STATUSES } from '../../common/consts';

export const useBlogsList = () => {
  return useListQuery<Blog>(apiUrls.BLOGS.LIST);
};

export const useBlogDetails = (pk?: PK) => {
  return useDetailsQuery<Blog>({
    queryKey: [apiUrls.BLOGS.DETAILS, { pk }],
    pk,
  });
};

type BlogUpdateVars = Partial<BlogCreateVars & CommonVars> & {
  status?: STATUSES;
};

export const useBlogUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<Blog, StandardError, BlogUpdateVars>(
    ({ pk, ...data }) =>
      Axios.patch(composeUrl(apiUrls.BLOGS.DETAILS, { params: { pk } }), data),
    {
      onSuccess: (data) => {
        queryClient.setQueryData([apiUrls.BLOGS.DETAILS, { pk: data.pk }], data);
        queryClient.invalidateQueries(apiUrls.BLOGS.LIST);
      },
    },
  );
};

export const useBlogCreate = () => {
  const queryClient = useQueryClient();
  return useMutation<Blog, StandardError, BlogCreateVars>(
    (data) => Axios.post(apiUrls.BLOGS.LIST, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiUrls.BLOGS.LIST);
        queryClient.invalidateQueries(apiUrls.CONTENT.LIST);
      },
    },
  );
};

export const useBlogTags = (pk?: PK) => {
  return useDetailsQuery<Tags>({ pk, queryKey: [apiUrls.BLOGS.TAGS, { pk }] });
};

interface BlogTagsUpdateVars extends CommonVars {
  payload: Tags;
}

export const useBlogTagsUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, StandardError, BlogTagsUpdateVars>(
    ({ pk, ...payload }) =>
      Axios.put(composeUrl(apiUrls.BLOGS.TAGS, { params: { pk } }), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiUrls.BLOGS.TAGS);
      },
    },
  );
};
